import { Card, CardGroup } from '@ps/sports-ui-component-library';
import { Translation } from '@ps/web-shared-components';
import { Loader } from '@ps/web-shared-ui';
import React, { memo, useMemo } from 'react';

import LoadingIndicator from '@swf/components/LoadingIndicator/LoadingIndicator';
import { Message, TryAgainMessage } from '@swf/sports/components';
import useScoreboardPolling from '@swf/sports/hooks/useScoreboardPolling/useScoreboardPolling';

import useCashOutPoller from '../../hooks/useCashOutPoller';
import useFetchMyBets from '../../hooks/useFetchMyBets';
import { getEventTypesWithEventIds } from '../../utils/getEventTypesWithEventIds';

import {
  TXTWCL_No_Settled_Bets_found_in_that__ELL,
  TXTWCL_Show_More,
  TXTWCL_SorryX_something_went_wrongX_P_ELL,
  TXTWCL_You_donXt_currently_have_any_O_ELL
} from './MyBets.messages.json';
import styles from './MyBets.scss';
import Bet from './components/Bet/Bet';

interface Props {
  isSettled?: boolean;
  fromDate?: Date;
  toDate?: Date;
  cashedOut?: Record<string, number | undefined>;
}

export const MyBets: React.FC<Props> = ({ isSettled, fromDate, toDate, cashedOut }) => {
  const { bets, moreAvailable, isFetching, fetchBets, fetchMoreBets, isErroneous } = useFetchMyBets(
    isSettled,
    fromDate,
    toDate
  );

  useCashOutPoller(isSettled);

  const eventTypesWithEventIds = useMemo(() => getEventTypesWithEventIds(bets), [bets]);

  useScoreboardPolling(eventTypesWithEventIds);

  if (!bets.length && isFetching) {
    return <LoadingIndicator loading className={styles.loading} />;
  }

  if (!bets.length && isErroneous) {
    return <TryAgainMessage onRetry={() => fetchBets({ fromRecord: 1 })} />;
  }

  if (bets.length > 0) {
    return (
      <CardGroup padded>
        {bets.map((bet) => (
          <Bet
            key={bet.betId}
            bet={bet}
            cashedOutAmount={cashedOut ? cashedOut[bet.betId] : 0}
            open
          />
        ))}
        {isErroneous && (
          <p role="alert" className={styles.showMoreError}>
            <Translation {...TXTWCL_SorryX_something_went_wrongX_P_ELL} />
          </p>
        )}
        {moreAvailable && (
          // DesignSystem: This button does not conform to any rules currently - the fact
          // it looks like a card is very odd. Needs looking at.
          <Card
            padded
            as="button"
            type="button"
            className={styles.showMore}
            onClick={fetchMoreBets}
            disabled={isFetching}
          >
            {isFetching ? <Loader /> : <Translation {...TXTWCL_Show_More} />}
          </Card>
        )}
      </CardGroup>
    );
  }

  return (
    <Message
      message={
        <Translation
          {...(isSettled
            ? TXTWCL_No_Settled_Bets_found_in_that__ELL
            : TXTWCL_You_donXt_currently_have_any_O_ELL)}
        />
      }
    />
  );
};

export default memo(MyBets);
